import { motion, AnimatePresence } from "framer-motion";
import { X, User, Building, Shield, CreditCard, Sparkles, Camera, Monitor, Download, FileSpreadsheet, FileText, Share2, Bell, CheckCircle2, KeyRound, Smartphone } from "lucide-react";
import { useState, useEffect, useRef, useMemo } from "react";
import { cn } from "@/lib/utils";
import { useClerk, useUser } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { useSubscriptions } from "@/context/SubscriptionContext";
import { showSuccess, showError } from "@/utils/toast";
import { backendFetch } from "@/lib/backend";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import Loader from "@/components/ui/loader-4";
import { useGsapActionMagnet, useGsapTogglePulse } from "@/hooks/useOrizonteMotion";
import { getPricingPlan, pricingPlans, type PlanKey } from "@/lib/pricing";
import { useTheme } from "next-themes";
import { downloadSubscriptionsCsv, downloadSubscriptionsPdf } from "@/lib/export";
import { useEntitlements } from "@/lib/entitlements";
import { buildOrizonte12Pulse } from "@/lib/orizonte-12";

const budgetCategories = ["Software", "AI", "Productivity", "Design", "Streaming", "Finance"];
const currencyOptions = ["USD", "EUR", "GBP", "JPY", "CAD", "AUD"];

const tabs = [
  { id: "profile", label: "Profile", icon: User },
  { id: "appearance", label: "Appearance", icon: Monitor },
  { id: "institutions", label: "Connected Accounts", icon: Building },
  { id: "preferences", label: "AI Preferences", icon: Sparkles },
  { id: "billing", label: "Billing", icon: CreditCard },
  { id: "security", label: "Security", icon: Shield },
];

export const SettingsModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState("preferences");
  const { user } = useUser();
  const { openUserProfile } = useClerk();
  const { theme, setTheme, resolvedTheme } = useTheme();
  const navigate = useNavigate();
  const { clearData, subscriptions } = useSubscriptions();
  const { hasFeature, requiredPlanFor } = useEntitlements();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [autonomousCancellation, setAutonomousCancellation] = useState(true);
  const [conciergeInterception, setConciergeInterception] = useState(true);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [notificationThreshold, setNotificationThreshold] = useState(60);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [renewalReminderDays, setRenewalReminderDays] = useState<3 | 7 | 30>(7);
  const [preferredCurrency, setPreferredCurrency] = useState("USD");
  const [categoryBudgets, setCategoryBudgets] = useState<Record<string, string>>({});
  const [analyticsConsent, setAnalyticsConsent] = useState(true);
  const [aiAutonomyLevel, setAiAutonomyLevel] = useState<"advisor" | "concierge" | "autonomous">("advisor");
  const [smartNotificationRules, setSmartNotificationRules] = useState<Record<string, boolean>>({});
  const [auditEvents, setAuditEvents] = useState<Array<{
    id: string;
    action: string;
    resourceType: string;
    resourceId?: string | null;
    createdAt: string;
  }>>([]);
  const pulse12 = useMemo(() => buildOrizonte12Pulse(subscriptions, smartNotificationRules), [subscriptions, smartNotificationRules]);
  const [billingStatus, setBillingStatus] = useState("free_beta");
  const [billingPlanKey, setBillingPlanKey] = useState<PlanKey>("flow");
  const [billingAction, setBillingAction] = useState<string | null>(null);
  const [billingConfigured, setBillingConfigured] = useState(false);
  const [providerCustomerId, setProviderCustomerId] = useState<string | null>(null);
  const [renewalDate, setRenewalDate] = useState<string | null>(null);
  const [exporting, setExporting] = useState<"csv" | "pdf" | null>(null);
  const [publicUsername, setPublicUsername] = useState("");
  const [publicBio, setPublicBio] = useState("");
  const [isPublicStack, setIsPublicStack] = useState(false);
  const [showPublicPrices, setShowPublicPrices] = useState(false);
  const [allowPublicClone, setAllowPublicClone] = useState(true);
  const [isSavingPublicStack, setIsSavingPublicStack] = useState(false);
  const { isEnabled } = useFeatureFlags();
  const billingFeatureEnabled = isEnabled("billing");
  useGsapTogglePulse(panelRef, [
    isOpen,
    activeTab,
    autonomousCancellation,
    conciergeInterception,
    emailNotifications,
    renewalReminderDays,
    analyticsConsent,
    aiAutonomyLevel,
  ]);
  useGsapActionMagnet(panelRef, "[data-gsap-magnet]", [isOpen, activeTab]);

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || "");
      setLastName(user.lastName || "");
      setEmail(user.primaryEmailAddress?.emailAddress || "");
    }
  }, [user]);

  useEffect(() => {
    if (!isOpen || !user) return;

    let cancelled = false;
    backendFetch('/settings')
      .then((data) => {
        if (cancelled) return;
        const settings = data.settings || {};
        setAutonomousCancellation(Boolean(settings.autonomousCancellation));
        setConciergeInterception(Boolean(settings.conciergeInterception));
        setTwoFactorEnabled(Boolean(settings.twoFactorEnabled));
        setNotificationThreshold(Number(settings.notificationThreshold ?? 60));
        setEmailNotifications(Boolean(settings.emailNotifications ?? true));
        setRenewalReminderDays((settings.renewalReminderDays === 3 || settings.renewalReminderDays === 30 ? settings.renewalReminderDays : 7) as 3 | 7 | 30);
        setPreferredCurrency(String(settings.preferredCurrency || "USD"));
        setAnalyticsConsent(Boolean(settings.analyticsConsent ?? true));
        setAiAutonomyLevel((settings.aiAutonomyLevel as "advisor" | "concierge" | "autonomous") || "advisor");
        setSmartNotificationRules(
          typeof settings.smartNotificationRules === "object" && settings.smartNotificationRules !== null
            ? settings.smartNotificationRules as Record<string, boolean>
            : {}
        );
      })
      .catch(console.error);

    backendFetch('/billing')
      .then((data) => {
        if (cancelled) return;
        setBillingStatus(data.billing?.status || "free_beta");
        setBillingPlanKey((data.billing?.planKey || "flow") as PlanKey);
        setBillingConfigured(Boolean(data.billingConfigured) && Boolean(data.billingEnabled));
        setProviderCustomerId(data.billing?.providerCustomerId || null);
        setRenewalDate(data.billing?.currentPeriodEnd || null);
      })
      .catch(console.error);

    backendFetch('/settings/category-budgets', { staleTtlMs: 60_000 })
      .then((data) => {
        if (cancelled) return;
        const next: Record<string, string> = {};
        for (const budget of (Array.isArray(data.budgets) ? data.budgets : [])) {
          next[String(budget.category)] = String(Number(budget.monthlyLimit || 0));
        }
        setCategoryBudgets(next);
      })
      .catch(console.error);

    backendFetch('/profile/public-stack', { staleTtlMs: 60_000 })
      .then((data) => {
        if (cancelled) return;
        const publicProfile = data.publicProfile || {};
        setPublicUsername(String(publicProfile.username || ""));
        setPublicBio(String(publicProfile.bio || ""));
        setIsPublicStack(Boolean(publicProfile.isPublic));
        setShowPublicPrices(Boolean(publicProfile.showPrices));
        setAllowPublicClone(publicProfile.allowClone !== false);
      })
      .catch(console.error);

    backendFetch('/settings/audit-events', { staleTtlMs: 30_000 })
      .then((data) => {
        if (cancelled) return;
        setAuditEvents(Array.isArray(data.auditEvents) ? data.auditEvents : []);
      })
      .catch(console.error);

    return () => {
      cancelled = true;
    };
  }, [isOpen, user]);

  const handleSaveProfile = async () => {
    if (!user) return;
    setIsSavingProfile(true);
    try {
      await user.update({ firstName, lastName });
      showSuccess("Profile updated successfully");
    } catch (err: unknown) {
      showError(err instanceof Error ? err.message : "Failed to update profile");
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setIsUploadingAvatar(true);
    try {
      await user.setProfileImage({ file });
      showSuccess("Profile picture updated");
    } catch (err: unknown) {
      showError(err instanceof Error ? err.message : "Failed to upload picture");
    } finally {
      setIsUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const openAccounts = (message: string) => {
    showSuccess(message);
    onClose();
    navigate("/accounts");
  };

  const openBillingFlow = async (planKey: PlanKey = billingPlanKey) => {
    try {
      setBillingAction(planKey);
      const endpoint = providerCustomerId && billingStatus !== "free_beta" ? "/billing/portal" : "/billing/checkout";
      const data = await backendFetch(endpoint, {
        method: "POST",
        body: JSON.stringify({ planKey }),
      });
      if (data.url) {
        window.location.assign(data.url);
        return;
      }
      showError("Billing provider did not return a redirect URL");
    } catch (error) {
      showError(error instanceof Error ? error.message : "Billing is not available in this environment");
    } finally {
      setBillingAction(null);
    }
  };

  const selectBetaPlan = async (planKey: PlanKey) => {
    try {
      setBillingAction(planKey);
      const data = await backendFetch('/billing/select-plan', {
        method: 'POST',
        body: JSON.stringify({ planKey }),
      });
      setBillingPlanKey((data.billing?.planKey || planKey) as PlanKey);
      setBillingStatus(data.billing?.status || "beta_reserved");
      showSuccess(planKey === "core" ? "Core invite request saved" : `${getPricingPlan(planKey).name} plan saved`);
    } catch (error) {
      showError(error instanceof Error ? error.message : "Plan could not be saved");
    } finally {
      setBillingAction(null);
    }
  };

  const handleDeleteData = async () => {
    const confirmed = window.confirm("Delete all subscription data from this account?");
    if (!confirmed) return;

    try {
      await clearData();
      showSuccess("All subscription data cleared");
    } catch (err: unknown) {
      showError(err instanceof Error ? err.message : "Failed to clear data");
    }
  };

  const handleExport = async (format: "csv" | "pdf") => {
    if (!hasFeature("exports")) {
      navigate(`/pricing?plan=${requiredPlanFor("exports")}`);
      return;
    }
    setExporting(format);
    try {
      if (format === "csv") {
        await downloadSubscriptionsCsv();
      } else {
        await downloadSubscriptionsPdf();
      }
      showSuccess(format === "csv" ? "CSV export downloaded" : "PDF report downloaded");
    } catch (error) {
      showError(error instanceof Error ? error.message : "Export could not be downloaded");
    } finally {
      setExporting(null);
    }
  };

  const persistSettings = async (next: {
    autonomousCancellation?: boolean;
    conciergeInterception?: boolean;
    twoFactorEnabled?: boolean;
    notificationThreshold?: number;
    emailNotifications?: boolean;
    renewalReminderDays?: 3 | 7 | 30;
    preferredCurrency?: string;
    analyticsConsent?: boolean;
    aiAutonomyLevel?: "advisor" | "concierge" | "autonomous";
    smartNotificationRules?: Record<string, boolean>;
  }) => {
    if (!user) return;
    try {
      await backendFetch('/settings', {
        method: 'PATCH',
        body: JSON.stringify(next),
      });
    } catch (error) {
      showError(error instanceof Error ? error.message : "Settings could not be saved");
      throw error;
    }
  };

  const toggleSmartRule = (id: string, enabled: boolean) => {
    const next = { ...smartNotificationRules, [id]: enabled };
    setSmartNotificationRules(next);
    persistSettings({ smartNotificationRules: next }).catch(() => {});
    showSuccess(`${enabled ? "Enabled" : "Disabled"} smart rule`);
  };

  const persistCategoryBudget = async (category: string, value: string) => {
    const monthlyLimit = Number(value || 0);
    if (!Number.isFinite(monthlyLimit) || monthlyLimit < 0) {
      showError("Enter a valid budget amount");
      return;
    }
    try {
      await backendFetch('/settings/category-budgets', {
        method: 'PATCH',
        body: JSON.stringify({ category, monthlyLimit }),
      });
      showSuccess(monthlyLimit > 0 ? `${category} budget saved` : `${category} budget cleared`);
    } catch (error) {
      showError(error instanceof Error ? error.message : "Budget could not be saved");
    }
  };

  const savePublicStack = async () => {
    setIsSavingPublicStack(true);
    try {
      const data = await backendFetch('/profile/public-stack', {
        method: 'PATCH',
        body: JSON.stringify({
          username: publicUsername.trim().toLowerCase(),
          bio: publicBio.trim(),
          isPublic: isPublicStack,
          showPrices: showPublicPrices,
          allowClone: allowPublicClone,
        }),
      });
      const publicProfile = data.publicProfile || {};
      setPublicUsername(String(publicProfile.username || publicUsername));
      showSuccess(isPublicStack ? "Public stack updated" : "Public stack saved as private");
    } catch (error) {
      showError(error instanceof Error ? error.message : "Public stack could not be saved");
    } finally {
      setIsSavingPublicStack(false);
    }
  };

  const initials = (user?.fullName || user?.primaryEmailAddress?.emailAddress || "U")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const formatAuditTime = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Recent";
    return date.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-md"
            onClick={onClose}
          />
          <motion.div
            ref={panelRef}
            initial={{ opacity: 0, scale: 0.95, x: "-50%", y: "-48%" }}
            animate={{ opacity: 1, scale: 1, x: "-50%", y: "-50%" }}
            exit={{ opacity: 0, scale: 0.95, x: "-50%", y: "-48%" }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed left-1/2 top-1/2 z-[80] w-[95%] md:w-full max-w-4xl h-[85dvh] md:h-[600px] overflow-hidden rounded-2xl border border-orizonte-border bg-orizonte-surface shadow-2xl flex flex-col md:flex-row"
          >
            <div className="w-full md:w-64 border-b md:border-b-0 md:border-r border-white/[0.05] bg-white/[0.01] px-3 py-3 md:p-4 flex flex-col shrink-0">
              <div className="flex items-center justify-between mb-3 md:mb-8 px-1 md:px-2 mt-1 md:mt-2">
                <h2 className="text-lg font-semibold text-orizonte-text tracking-tight">Settings</h2>
                <button 
                  onClick={onClose} 
                  className="md:hidden p-1.5 text-orizonte-textMuted hover:text-white transition-colors rounded-lg hover:bg-white/5"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <nav className="flex md:flex-col gap-1.5 md:gap-1 overflow-x-auto pb-1 md:pb-0 scrollbar-hide -mx-1 px-1" style={{ scrollbarWidth: 'none' }}>
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    data-gsap-magnet
                    className={cn(
                      "flex-shrink-0 flex items-center gap-2 md:gap-3 px-2.5 md:px-3 py-2 md:py-2.5 rounded-lg text-sm font-medium transition-colors relative",
                      activeTab === tab.id 
                        ? "text-orizonte-lime bg-orizonte-lime/10" 
                        : "text-orizonte-textMuted hover:text-orizonte-text hover:bg-white/[0.04]"
                    )}
                  >
                    <tab.icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{tab.label}</span>
                    <span className="sm:hidden text-xs">{tab.label.split(' ')[0]}</span>
                    {activeTab === tab.id && (
                      <motion.div 
                        layoutId="active-tab-indicator"
                        className="absolute bottom-0 left-3 right-3 h-0.5 md:bottom-auto md:left-0 md:top-1/2 md:-translate-y-1/2 md:w-1 md:h-6 bg-orizonte-lime md:rounded-r-full shadow-[0_0_10px_rgba(204,255,0,0.5)]"
                      />
                    )}
                  </button>
                ))}
              </nav>
            </div>

            <div className="flex-1 flex flex-col bg-orizonte-bg/50 relative overflow-hidden">
              <button 
                onClick={onClose} 
                data-gsap-magnet
                className="hidden md:block absolute top-4 right-4 p-2 text-orizonte-textMuted hover:text-white transition-colors rounded-lg hover:bg-white/5 z-10"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="p-3 sm:p-6 md:p-8 flex-1 overflow-y-auto">
                <AnimatePresence mode="wait">
                  
                  {activeTab === "preferences" && (
                    <motion.div 
                      key="preferences"
                      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                      className="max-w-xl space-y-8"
                    >
                      <div>
                        <h3 className="text-xl font-semibold text-orizonte-text mb-1">AI Action Preferences</h3>
                        <p className="text-sm text-orizonte-textMuted">Configure how aggressively Orizonte manages your stack.</p>
                      </div>

                      <div className="space-y-6">
                        <div className="bg-white/[0.02] border border-white/[0.05] rounded-xl p-4 sm:p-5">
                          <div className="flex justify-between items-start mb-4">
                            <div className="pr-4">
                              <h4 className="text-sm font-medium text-orizonte-text">Autonomous Cancellation</h4>
                              <p className="text-xs text-orizonte-textMuted mt-1 max-w-[280px]">Allow AI to cancel services automatically if unused for 90 days.</p>
                            </div>
                            <button
                              type="button"
                              data-settings-toggle
                              data-gsap-magnet
                              onClick={() => {
                                const next = !autonomousCancellation;
                                setAutonomousCancellation(next);
                                persistSettings({ autonomousCancellation: next }).catch(() => {});
                                showSuccess(`Autonomous cancellation ${next ? "enabled" : "disabled"}`);
                              }}
                              className={cn(
                                "w-11 h-6 rounded-full relative border shrink-0 transition-colors",
                                autonomousCancellation ? "is-on bg-orizonte-lime/20 border-orizonte-lime/30" : "bg-white/[0.1] border-white/[0.05]"
                              )}
                            >
                              <div className={cn(
                                "w-4 h-4 rounded-full absolute top-1 transition-all",
                                autonomousCancellation ? "left-6 bg-orizonte-lime" : "left-1 bg-orizonte-textMuted"
                              )} />
                            </button>
                          </div>
                          <div className="flex justify-between items-start pt-4 border-t border-white/[0.05]">
                            <div className="pr-4">
                              <h4 className="text-sm font-medium text-orizonte-text">Concierge Interception</h4>
                              <p className="text-xs text-orizonte-textMuted mt-1 max-w-[280px]">Enable AI to negotiate retention discounts during cancellation flows.</p>
                            </div>
                            <button
                              type="button"
                              data-settings-toggle
                              data-gsap-magnet
                              onClick={() => {
                                const next = !conciergeInterception;
                                setConciergeInterception(next);
                                persistSettings({ conciergeInterception: next }).catch(() => {});
                                showSuccess(`Concierge interception ${next ? "enabled" : "disabled"}`);
                              }}
                              className={cn(
                                "w-11 h-6 rounded-full relative border shrink-0 transition-colors",
                                conciergeInterception ? "is-on bg-orizonte-lime/20 border-orizonte-lime/30" : "bg-white/[0.1] border-white/[0.05]"
                              )}
                            >
                              <div className={cn(
                                "w-4 h-4 rounded-full absolute top-1 transition-all shadow-[0_0_8px_rgba(204,255,0,0.6)]",
                                conciergeInterception ? "left-6 bg-orizonte-lime" : "left-1 bg-orizonte-textMuted"
                              )} />
                            </button>
                          </div>
                        </div>

                        <div>
                          <div className="mb-6">
                            <label className="block text-sm font-medium text-orizonte-text mb-3">AI Autonomy Level</label>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                              {(["advisor", "concierge", "autonomous"] as const).map((level) => (
                                <button
                                  key={level}
                                  type="button"
                                  data-settings-toggle
                                  data-gsap-magnet
                                  onClick={() => {
                                    setAiAutonomyLevel(level);
                                    persistSettings({ aiAutonomyLevel: level }).catch(console.error);
                                    showSuccess(`AI mode set to ${level}`);
                                  }}
                                  className={cn(
                                    "rounded-xl border px-3 py-3 text-left text-sm capitalize transition-colors",
                                    aiAutonomyLevel === level
                                      ? "is-on border-orizonte-lime/40 bg-orizonte-lime/10 text-orizonte-lime"
                                      : "border-white/10 bg-white/[0.02] text-orizonte-textMuted hover:text-orizonte-text"
                                  )}
                                >
                                  {level}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="flex justify-between items-center mb-3">
                            <label className="block text-sm font-medium text-orizonte-text">Notification Threshold</label>
                            <span className="text-xs font-mono text-orizonte-lime">{notificationThreshold}%</span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={notificationThreshold}
                            onChange={(e) => {
                              const val = Number(e.target.value);
                              setNotificationThreshold(val);
                              persistSettings({ notificationThreshold: val }).catch(console.error);
                            }}
                            className="w-full h-1.5 bg-white/10 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-orizonte-lime [&::-webkit-slider-thumb]:shadow-[0_0_10px_rgba(204,255,0,0.5)]"
                          />
                          <div className="flex justify-between text-xs text-orizonte-textMuted mt-1">
                            <span>Quiet</span>
                            <span>Aggressive</span>
                          </div>
                          <p className="text-xs text-orizonte-textMuted mt-3">Controls how often the intelligence engine notifies you about your stack.</p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <button
                            type="button"
                            data-settings-toggle
                            data-gsap-magnet
                            onClick={() => {
                              const next = !emailNotifications;
                              setEmailNotifications(next);
                              persistSettings({ emailNotifications: next }).catch(console.error);
                              showSuccess(`Email notifications ${next ? "enabled" : "disabled"}`);
                            }}
                            className={cn(
                              "rounded-xl border p-4 text-left transition-colors",
                              emailNotifications ? "is-on border-orizonte-emerald/30 bg-orizonte-emerald/10" : "border-white/10 bg-white/[0.02]"
                            )}
                          >
                            <span className="block text-sm font-medium text-orizonte-text">Email reminders</span>
                            <span className="mt-1 block text-xs text-orizonte-textMuted">Renewals and account events through Resend.</span>
                          </button>
                          <button
                            type="button"
                            data-settings-toggle
                            data-gsap-magnet
                            onClick={() => {
                              const next = !analyticsConsent;
                              setAnalyticsConsent(next);
                              persistSettings({ analyticsConsent: next }).catch(console.error);
                              showSuccess(`Product analytics ${next ? "enabled" : "disabled"}`);
                            }}
                            className={cn(
                              "rounded-xl border p-4 text-left transition-colors",
                              analyticsConsent ? "is-on border-orizonte-lime/30 bg-orizonte-lime/10" : "border-white/10 bg-white/[0.02]"
                            )}
                          >
                            <span className="block text-sm font-medium text-orizonte-text">Product analytics</span>
                            <span className="mt-1 block text-xs text-orizonte-textMuted">Usage signals without sensitive billing text.</span>
                          </button>
                        </div>

                        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                              <h4 className="text-sm font-medium text-orizonte-text">Renewal alerts</h4>
                              <p className="mt-1 text-xs text-orizonte-textMuted">Email before an upcoming renewal when email reminders are enabled.</p>
                            </div>
                            <div className="grid grid-cols-3 gap-1 rounded-xl border border-white/10 bg-black/10 p-1">
                              {([3, 7, 30] as const).map((days) => (
                                <button
                                  key={days}
                                  type="button"
                                  data-settings-toggle
                                  data-gsap-magnet
                                  onClick={() => {
                                    setRenewalReminderDays(days);
                                    persistSettings({ renewalReminderDays: days }).catch(console.error);
                                    showSuccess(`Renewal alerts set to ${days} days`);
                                  }}
                                  className={cn(
                                    "rounded-lg px-3 py-2 text-xs font-semibold transition-colors",
                                    renewalReminderDays === days
                                      ? "is-on bg-orizonte-lime text-black"
                                      : "text-orizonte-textMuted hover:text-orizonte-text"
                                  )}
                                >
                                  {days}d
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className="rounded-xl border border-orizonte-lime/20 bg-orizonte-lime/[0.04] p-4">
                          <div className="mb-4 flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-orizonte-lime/20 bg-orizonte-lime/10 text-orizonte-lime">
                              <Bell className="h-4 w-4" />
                            </div>
                            <div>
                              <h4 className="text-sm font-medium text-orizonte-text">Orizonte 1.2 smart rules</h4>
                              <p className="text-xs text-orizonte-textMuted">Live rules generated from renewal, trial, overlap, and spend pressure.</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {pulse12.smartRules.map((rule) => (
                              <button
                                key={rule.id}
                                type="button"
                                data-settings-toggle
                                data-gsap-magnet
                                onClick={() => toggleSmartRule(rule.id, !rule.enabled)}
                                className={cn(
                                  "rounded-xl border p-3 text-left transition-colors",
                                  rule.enabled
                                    ? "is-on border-orizonte-lime/20 bg-black/20"
                                    : "border-white/10 bg-black/10 opacity-70 hover:opacity-100"
                                )}
                              >
                                <div className="flex items-center justify-between gap-3">
                                  <p className="text-xs font-semibold text-orizonte-text">{rule.title}</p>
                                  <span className={cn(
                                    "text-[10px] font-semibold uppercase tracking-widest",
                                    rule.enabled ? "text-orizonte-lime" : "text-orizonte-textMuted"
                                  )}>
                                    {rule.enabled ? "on" : "off"}
                                  </span>
                                </div>
                                <p className="mt-1 text-xs leading-relaxed text-orizonte-textMuted">{rule.description}</p>
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                          <div className="mb-4">
                            <h4 className="text-sm font-medium text-orizonte-text">Category budgets</h4>
                            <p className="mt-1 text-xs text-orizonte-textMuted">Monthly limits power Analytics budget bars and warning notifications.</p>
                          </div>
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {budgetCategories.map((category) => (
                              <label key={category} className="space-y-1.5">
                                <span className="text-xs font-medium text-orizonte-textMuted">{category}</span>
                                <div className="flex rounded-lg border border-white/10 bg-black/20 focus-within:border-orizonte-lime/50">
                                  <span className="flex items-center px-3 text-sm text-orizonte-textMuted">$</span>
                                  <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={categoryBudgets[category] || ""}
                                    onChange={(event) => setCategoryBudgets((current) => ({ ...current, [category]: event.target.value }))}
                                    onBlur={(event) => persistCategoryBudget(category, event.target.value)}
                                    className="min-w-0 flex-1 bg-transparent py-2 pr-3 text-sm text-orizonte-text outline-none"
                                    placeholder="0"
                                  />
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {activeTab === "profile" && (
                    <motion.div 
                      key="profile"
                      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                      className="max-w-xl space-y-8"
                    >
                      <div>
                        <h3 className="text-xl font-semibold text-orizonte-text mb-1">Personal Profile</h3>
                        <p className="text-sm text-orizonte-textMuted">Manage your identity and app preferences.</p>
                      </div>

                      <div className="flex items-center gap-6 pb-6 border-b border-white/[0.05]">
                        {/* Avatar with upload overlay */}
                        <button
                          type="button"
                          onClick={() => avatarInputRef.current?.click()}
                          disabled={isUploadingAvatar}
                          className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full shrink-0 group focus:outline-none"
                        >
                          {user?.imageUrl ? (
                            <img
                              src={user.imageUrl}
                              alt="Profile"
                              className="w-full h-full rounded-full object-cover shadow-[0_0_20px_rgba(204,255,0,0.15)]"
                            />
                          ) : (
                            <div className="w-full h-full rounded-full bg-gradient-to-br from-orizonte-lime to-orizonte-emerald flex items-center justify-center text-xl sm:text-2xl font-bold text-black shadow-[0_0_20px_rgba(204,255,0,0.2)]">
                              {initials}
                            </div>
                          )}
                          <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            {isUploadingAvatar ? (
                              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                              <Camera className="w-5 h-5 text-white" />
                            )}
                          </div>
                        </button>
                        <input
                          ref={avatarInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleAvatarChange}
                        />
                        <div>
                          <p className="text-sm font-medium text-orizonte-text mb-0.5">{user?.fullName || initials}</p>
                          <p className="text-xs text-orizonte-textMuted mb-2">{user?.primaryEmailAddress?.emailAddress}</p>
                          <button
                            type="button"
                            onClick={() => avatarInputRef.current?.click()}
                            className="text-xs text-orizonte-lime hover:text-orizonte-lime/80 transition-colors flex items-center gap-1"
                          >
                            <Camera className="w-3 h-3" /> Change picture
                          </button>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-orizonte-textMuted mb-1.5">First Name</label>
                            <input 
                              type="text" 
                              value={firstName}
                              onChange={(e) => setFirstName(e.target.value)}
                              className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-orizonte-text focus:outline-none focus:border-orizonte-lime/50 transition-colors" 
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-orizonte-textMuted mb-1.5">Last Name</label>
                            <input 
                              type="text" 
                              value={lastName}
                              onChange={(e) => setLastName(e.target.value)}
                              className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-orizonte-text focus:outline-none focus:border-orizonte-lime/50 transition-colors" 
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-orizonte-textMuted mb-1.5">Email Address</label>
                          <input 
                            type="email" 
                            value={email}
                            readOnly
                            className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-orizonte-text/50 cursor-not-allowed" 
                          />
                          <p className="text-[10px] text-orizonte-textMuted/50 mt-1">Email must be changed via your authentication provider.</p>
                        </div>
                      </div>

                      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 sm:p-5">
                        <div className="mb-4 flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 bg-white/[0.04] text-orizonte-lime">
                            <Share2 className="h-4 w-4" />
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-orizonte-text">Public stack profile</h4>
                            <p className="text-xs text-orizonte-textMuted">Publish a read-only stack at /u/{publicUsername || "username"}/stack.</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 gap-3">
                          <label className="space-y-1.5">
                            <span className="text-xs font-medium text-orizonte-textMuted">Username</span>
                            <input
                              value={publicUsername}
                              onChange={(event) => setPublicUsername(event.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))}
                              className="w-full rounded-lg border border-white/10 bg-black/20 px-4 py-2.5 text-sm text-orizonte-text focus:border-orizonte-lime/50 focus:outline-none"
                            />
                          </label>
                          <label className="space-y-1.5">
                            <span className="text-xs font-medium text-orizonte-textMuted">Bio</span>
                            <textarea
                              value={publicBio}
                              onChange={(event) => setPublicBio(event.target.value)}
                              rows={2}
                              className="w-full resize-none rounded-lg border border-white/10 bg-black/20 px-4 py-2.5 text-sm text-orizonte-text focus:border-orizonte-lime/50 focus:outline-none"
                              placeholder="What this stack is for"
                            />
                          </label>
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                            {[
                              { label: "Public", enabled: isPublicStack, set: setIsPublicStack },
                              { label: "Show prices", enabled: showPublicPrices, set: setShowPublicPrices },
                              { label: "Allow clone", enabled: allowPublicClone, set: setAllowPublicClone },
                            ].map((item) => (
                              <button
                                key={item.label}
                                type="button"
                                onClick={() => item.set(!item.enabled)}
                                className={cn(
                                  "rounded-xl border px-3 py-2 text-left text-xs font-semibold transition-colors",
                                  item.enabled ? "border-orizonte-lime/30 bg-orizonte-lime/10 text-orizonte-lime" : "border-white/10 bg-white/[0.02] text-orizonte-textMuted"
                                )}
                              >
                                {item.label}
                              </button>
                            ))}
                          </div>
                          <button
                            type="button"
                            onClick={savePublicStack}
                            disabled={isSavingPublicStack}
                            className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm font-semibold text-orizonte-text transition-colors hover:bg-white/[0.08] disabled:cursor-wait disabled:opacity-70"
                          >
                            {isSavingPublicStack ? <Loader size={4} /> : null}
                            Save Public Stack
                          </button>
                        </div>
                      </div>

                      <button 
                        onClick={handleSaveProfile}
                        disabled={isSavingProfile}
                        data-gsap-magnet
                        className="w-full sm:w-auto px-6 py-2.5 bg-orizonte-lime text-black rounded-lg text-sm font-semibold transition-colors hover:bg-orizonte-lime/90 shadow-[0_0_15px_rgba(204,255,0,0.2)] disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {isSavingProfile ? <Loader size={4} /> : null}
                        Save Changes
                      </button>
                    </motion.div>
                  )}

                  {activeTab === "appearance" && (
                    <motion.div
                      key="appearance"
                      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                      className="max-w-xl space-y-8"
                    >
                      <div>
                        <h3 className="text-xl font-semibold text-orizonte-text mb-1">Appearance & Data</h3>
                        <p className="text-sm text-orizonte-textMuted">Choose your workspace theme and download your subscription records.</p>
                      </div>

                      <div className="space-y-3">
                        <h4 className="text-sm font-medium text-orizonte-text">Theme</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          {(["dark", "light", "system"] as const).map((mode) => (
                            <button
                              key={mode}
                              type="button"
                              data-settings-toggle
                              data-gsap-magnet
                              onClick={() => {
                                setTheme(mode);
                                showSuccess(`Theme set to ${mode}`);
                              }}
                              className={cn(
                                "rounded-xl border px-4 py-3 text-left transition-colors",
                                theme === mode
                                  ? "is-on border-orizonte-lime/40 bg-orizonte-lime/10 text-orizonte-lime"
                                  : "border-white/10 bg-white/[0.02] text-orizonte-textMuted hover:text-orizonte-text"
                              )}
                            >
                              <span className="block text-sm font-semibold capitalize">{mode}</span>
                              <span className="mt-1 block text-xs text-orizonte-textMuted">
                                {mode === "system" ? `Using ${resolvedTheme || "system"}` : mode === "dark" ? "Default Orizonte glow" : "Bright workspace"}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-3">
                        <h4 className="text-sm font-medium text-orizonte-text">Preferred Currency</h4>
                        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                          {currencyOptions.map((code) => (
                            <button
                              key={code}
                              type="button"
                              data-settings-toggle
                              data-gsap-magnet
                              onClick={() => {
                                setPreferredCurrency(code);
                                persistSettings({ preferredCurrency: code }).catch(console.error);
                                showSuccess(`Currency set to ${code}`);
                              }}
                              className={cn(
                                "rounded-xl border px-3 py-2 text-sm font-semibold transition-colors",
                                preferredCurrency === code
                                  ? "is-on border-orizonte-lime/40 bg-orizonte-lime/10 text-orizonte-lime"
                                  : "border-white/10 bg-white/[0.02] text-orizonte-textMuted hover:text-orizonte-text"
                              )}
                            >
                              {code}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 sm:p-5">
                        <div className="mb-4 flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 bg-white/[0.04] text-orizonte-lime">
                            <Download className="h-4 w-4" />
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-orizonte-text">Export subscription data</h4>
                            <p className="text-xs text-orizonte-textMuted">
                              {hasFeature("exports") ? "Downloads use your authenticated privacy export." : "CSV and PDF reports require Pulse."}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <button
                            type="button"
                            data-gsap-magnet
                            onClick={() => handleExport("csv")}
                            disabled={exporting !== null}
                            className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm font-medium text-orizonte-text transition-colors hover:bg-white/[0.08] disabled:cursor-wait disabled:opacity-60"
                          >
                            {exporting === "csv" ? <Loader size={4} /> : <FileSpreadsheet className="h-4 w-4" />}
                            CSV
                          </button>
                          <button
                            type="button"
                            data-gsap-magnet
                            onClick={() => handleExport("pdf")}
                            disabled={exporting !== null}
                            className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm font-medium text-orizonte-text transition-colors hover:bg-white/[0.08] disabled:cursor-wait disabled:opacity-60"
                          >
                            {exporting === "pdf" ? <Loader size={4} /> : <FileText className="h-4 w-4" />}
                            PDF
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {activeTab === "institutions" && (
                    <motion.div 
                      key="institutions"
                      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                      className="max-w-xl space-y-8"
                    >
                      <div>
                        <h3 className="text-xl font-semibold text-orizonte-text mb-1">Connected Data Sources</h3>
                        <p className="text-sm text-orizonte-textMuted">
                          Link bank accounts and credit cards to automatically import transactions.
                        </p>
                      </div>

                      <div className="p-4 rounded-xl border border-white/10 bg-white/[0.02]">
                        <p className="text-xs text-orizonte-textMuted mb-3">Transaction provider ingestion is coming later. For now, you can manually add institutions.</p>
                        <button
                          type="button"
                          onClick={() => openAccounts("Opening institution management")}
                          data-gsap-magnet
                          className="w-full py-3 rounded-xl border border-dashed border-white/20 text-sm font-medium text-orizonte-textMuted hover:text-white hover:border-white/40 hover:bg-white/[0.02] transition-colors flex items-center justify-center gap-2"
                        >
                          <Building className="w-4 h-4" /> Manage Institutions
                        </button>
                      </div>
                    </motion.div>
                  )}

                  {activeTab === "billing" && (
                    <motion.div 
                      key="billing"
                      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                      className="max-w-3xl space-y-7"
                    >
                      <div>
                        <h3 className="text-xl font-semibold text-orizonte-text mb-1">SnapSolve Plan</h3>
                        <p className="text-sm text-orizonte-textMuted">Orizonte is the first public beta product in the SnapSolve ecosystem.</p>
                      </div>

                      <div className="p-6 rounded-2xl border border-white/10 bg-white/[0.02] relative overflow-hidden">
                        <div className="flex items-center gap-2 mb-2 text-orizonte-textMuted">
                          <Sparkles className="w-4 h-4" />
                          <span className="text-xs font-bold uppercase tracking-wider">Current Plan</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                          <div>
                            <h4 className="text-2xl font-bold text-orizonte-text">{getPricingPlan(billingPlanKey).name}</h4>
                            <p className="text-sm text-orizonte-textMuted mt-1">
                              {billingConfigured && billingFeatureEnabled
                                ? "Dodo Payments is configured. Checkout and customer portal sessions are live for configured products."
                                : "Paid plan choices are saved for beta access. Card charging remains behind the billing feature flag."}
                            </p>
                            <p className="mt-2 text-xs font-medium uppercase tracking-wider text-orizonte-lime">{billingStatus.replace(/_/g, " ")}</p>
                            {renewalDate && (
                              <p className="mt-1 text-xs text-orizonte-textMuted">
                                Renews {new Date(renewalDate).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => openBillingFlow(billingPlanKey)}
                            disabled={!billingConfigured || !billingFeatureEnabled || (billingPlanKey === "flow" && !providerCustomerId)}
                            data-gsap-magnet
                            className="w-full sm:w-auto px-4 py-2 bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 rounded-lg text-sm font-medium transition-colors text-orizonte-text"
                          >
                            {billingAction === billingPlanKey ? "Opening..." : providerCustomerId ? "Manage billing" : "Checkout"}
                          </button>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                        <h4 className="mb-5 text-sm font-semibold uppercase tracking-widest text-orizonte-textMuted">SnapSolve Ecosystem</h4>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                          {pricingPlans.map((plan) => {
                            const Icon = plan.icon;
                            const isOwned = billingPlanKey === plan.key;
                            return (
                              <div
                                key={plan.key}
                                className={cn(
                                  "flex items-start gap-3 rounded-xl border p-3.5 transition-colors",
                                  isOwned
                                    ? "border-orizonte-lime/30 bg-orizonte-lime/[0.06]"
                                    : "border-white/5 bg-white/[0.02] opacity-55"
                                )}
                              >
                                <div className={cn(
                                  "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border",
                                  isOwned
                                    ? "border-orizonte-lime/30 bg-orizonte-lime/10 text-orizonte-lime"
                                    : "border-white/10 bg-white/[0.04] text-orizonte-textMuted"
                                )}>
                                  <Icon className="h-5 w-5" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center justify-between gap-2">
                                    <p className="truncate text-sm font-semibold text-orizonte-text">{plan.ecosystemName}</p>
                                    {isOwned && (
                                      <span className="shrink-0 rounded-full bg-orizonte-lime/15 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-orizonte-lime">
                                        {billingPlanKey === "flow" && plan.key === "flow" ? "Free" : "Active"}
                                      </span>
                                    )}
                                  </div>
                                  <p className="mt-0.5 text-xs text-orizonte-textMuted">
                                    {plan.monthlyPrice === 0 ? "Free" : `$${plan.monthlyPrice}/mo`}
                                    {plan.availability === "invite_only" && " \u00b7 Invite only"}
                                    {plan.availability === "coming_soon" && " \u00b7 Coming soon"}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div>
                        <div className="mb-4">
                          <h4 className="text-sm font-medium text-orizonte-text">Plans</h4>
                          <p className="text-xs text-orizonte-textMuted mt-1">Flow is free. Pulse is the public beta paid plan. Core is invite-only. Orbit opens with Rallia and SideBy.</p>
                        </div>

                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          {pricingPlans.map((plan) => {
                            const Icon = plan.icon;
                            const isSelected = billingPlanKey === plan.key;
                            const disabled = plan.availability === "coming_soon";
                            return (
                              <div
                                key={plan.key}
                                className={cn(
                                  "rounded-xl border p-4 transition-colors",
                                  isSelected ? "border-orizonte-lime/40 bg-orizonte-lime/[0.04]" : "border-white/10 bg-white/[0.02]"
                                )}
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex items-center gap-3">
                                    <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/[0.04] text-orizonte-lime">
                                      <Icon className="h-4 w-4" />
                                    </div>
                                    <div>
                                      <p className="text-sm font-semibold text-orizonte-text">{plan.name}</p>
                                      <p className="text-xs text-orizonte-textMuted">{plan.tagline}</p>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-lg font-semibold text-orizonte-text">
                                      ${plan.monthlyPrice}
                                    </p>
                                    <p className="text-[11px] text-orizonte-textMuted">{plan.monthlyPrice === 0 ? "free" : "/mo"}</p>
                                  </div>
                                </div>
                                <p className="mt-3 min-h-10 text-xs leading-relaxed text-orizonte-textMuted">{plan.description}</p>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (billingFeatureEnabled && plan.availability === "live" && plan.monthlyPrice > 0) {
                                      openBillingFlow(plan.key);
                                      return;
                                    }
                                    selectBetaPlan(plan.key);
                                  }}
                                  disabled={disabled || billingAction === plan.key}
                                  data-gsap-magnet
                                  className={cn(
                                    "mt-4 h-9 w-full rounded-lg text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                                    isSelected
                                      ? "bg-orizonte-lime text-black"
                                      : "border border-white/10 bg-white/[0.04] text-orizonte-text hover:bg-white/[0.08]"
                                  )}
                                >
                                  {disabled ? "Coming soon" : billingAction === plan.key ? "Saving..." : isSelected ? "Selected" : plan.cta}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div className="p-4 rounded-xl border border-white/10 bg-white/[0.02] flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-8 rounded bg-white/10 flex items-center justify-center shrink-0">
                            <CreditCard className="w-5 h-5 text-white/50" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-orizonte-text">Payment method</p>
                            <p className="text-xs text-orizonte-textMuted mt-0.5">Cards and invoices are managed in Dodo Payments after checkout.</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => openAccounts("Opening payment update flow")}
                          data-gsap-magnet
                          className="text-xs text-orizonte-textMuted hover:text-white transition-colors"
                        >
                          Update
                        </button>
                      </div>
                    </motion.div>
                  )}

                  {activeTab === "security" && (
                    <motion.div 
                      key="security"
                      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                      className="max-w-xl space-y-8"
                    >
                      <div>
                        <h3 className="text-xl font-semibold text-orizonte-text mb-1">Security & Access</h3>
                        <p className="text-sm text-orizonte-textMuted">Manage MFA, active sessions, passwords, and connected login methods.</p>
                      </div>

                      <div className="space-y-6">
                        <div className="rounded-xl border border-orizonte-lime/20 bg-orizonte-lime/[0.04] p-4 sm:p-5">
                          <div className="mb-4 flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-orizonte-lime/20 bg-orizonte-lime/10 text-orizonte-lime">
                              <Shield className="h-4 w-4" />
                            </div>
                            <div>
                              <h4 className="text-sm font-medium text-orizonte-text">Orizonte 1.2 Security Center</h4>
                              <p className="text-xs text-orizonte-textMuted">Trust checks for auth, data controls, sessions, and mobile app readiness.</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {pulse12.securityCenter.map((item) => (
                              <div key={item.id} className="rounded-xl border border-white/10 bg-black/20 p-3">
                                <div className="mb-2 flex items-center gap-2">
                                  {item.status === "protected" ? (
                                    <CheckCircle2 className="h-3.5 w-3.5 text-orizonte-emerald" />
                                  ) : (
                                    <KeyRound className="h-3.5 w-3.5 text-yellow-300" />
                                  )}
                                  <p className="text-xs font-semibold text-orizonte-text">{item.title}</p>
                                </div>
                                <p className="text-xs leading-relaxed text-orizonte-textMuted">{item.description}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 sm:p-5">
                          <div className="mb-4 flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 bg-white/[0.04] text-orizonte-lime">
                              <KeyRound className="h-4 w-4" />
                            </div>
                            <div>
                              <h4 className="text-sm font-medium text-orizonte-text">Recent security activity</h4>
                              <p className="text-xs text-orizonte-textMuted">Audit events from settings, exports, billing, and data controls.</p>
                            </div>
                          </div>
                          {auditEvents.length === 0 ? (
                            <p className="rounded-xl border border-white/10 bg-black/20 p-3 text-xs text-orizonte-textMuted">
                              No recent account security events yet.
                            </p>
                          ) : (
                            <div className="space-y-2">
                              {auditEvents.slice(0, 5).map((event) => (
                                <div key={event.id} className="rounded-xl border border-white/10 bg-black/20 p-3">
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <p className="truncate text-xs font-semibold text-orizonte-text">{event.action.replace(/\./g, " ")}</p>
                                      <p className="mt-1 text-[10px] uppercase tracking-widest text-orizonte-textMuted">
                                        {event.resourceType}{event.resourceId ? ` · ${event.resourceId}` : ""}
                                      </p>
                                    </div>
                                    <span className="shrink-0 text-[10px] text-orizonte-textMuted">{formatAuditTime(event.createdAt)}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 sm:p-5">
                          <div className="mb-4 flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 bg-white/[0.04] text-orizonte-lime">
                              <Smartphone className="h-4 w-4" />
                            </div>
                            <div>
                              <h4 className="text-sm font-medium text-orizonte-text">PWA and mobile readiness</h4>
                              <p className="text-xs text-orizonte-textMuted">Installable shell, mobile nav, app shortcuts, and prompt guidance.</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {pulse12.pwaReadiness.map((item) => (
                              <div key={item.id} className="rounded-xl border border-white/10 bg-black/20 p-3">
                                <div className="flex items-center justify-between gap-3">
                                  <p className="text-xs font-semibold text-orizonte-text">{item.title}</p>
                                  <span className="text-[10px] font-semibold uppercase tracking-widest text-orizonte-emerald">
                                    {item.ready ? "ready" : "review"}
                                  </span>
                                </div>
                                <p className="mt-1 text-xs leading-relaxed text-orizonte-textMuted">{item.description}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="bg-white/[0.02] border border-white/[0.05] rounded-xl p-4 sm:p-5">
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
                            <div>
                              <h4 className="text-sm font-medium text-orizonte-text">Two-Factor Authentication</h4>
                              <p className="text-xs text-orizonte-textMuted mt-1">Add an extra layer of security to your account via Clerk.</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => openUserProfile()}
                              className="w-full sm:w-auto inline-flex items-center gap-2 px-4 py-2 bg-orizonte-lime/10 text-orizonte-lime border border-orizonte-lime/20 rounded-lg text-sm font-medium transition-colors hover:bg-orizonte-lime/20"
                            >
                              Open security center <Shield className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <div className="flex flex-col gap-4 pt-6 border-t border-white/[0.05]">
                            <div>
                              <h4 className="text-sm font-medium text-orizonte-text mb-3">Session Management</h4>
                              <p className="text-xs text-orizonte-textMuted">
                                Review active sessions, revoke devices, update passwords, and configure second factors in the Clerk security center.
                              </p>
                            </div>
                          </div>
                        </div>

                        <div>
                          <h4 className="text-sm font-medium text-red-400 mb-2">Danger Zone</h4>
                          <div className="p-4 sm:p-5 rounded-xl border border-red-500/20 bg-red-500/[0.02] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div>
                              <h4 className="text-sm font-medium text-orizonte-text">Clear Subscription Data</h4>
                              <p className="text-xs text-orizonte-textMuted mt-1 max-w-[280px]">Remove all subscription records from your account. This action cannot be undone.</p>
                            </div>
                            <button
                              type="button"
                              onClick={handleDeleteData}
                              data-gsap-magnet
                              className="w-full sm:w-auto px-4 py-2 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg text-sm font-medium transition-colors hover:bg-red-500/20"
                            >
                              Clear Data
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                  
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
